// useJanetUnread — conversations Janet started that the signed-in person
// hasn't opened yet (janet_chat_sessions.unread, own rows under RLS), for the
// rail and the floating button: how many, and the newest one to open.
//
// Re-reads when any topic changes in this window ('janet:sessions-changed'),
// when another window bumps the sessions tick, and every few minutes so a
// conversation the worker opened appears without a reload. Before migration
// 20261012c the column is missing and this reports nothing.

import { useCallback, useEffect, useState } from 'react'
import supabase from '../lib/supabase'
import { useAuth } from '../contexts/AuthContext'
import { JANET_SESSIONS_TICK_KEY, notifySessionsChanged } from './useJanetSessions'

const POLL_MS = 4 * 60_000

/**
 * A topic on screen is read — whichever host shows it (the /janet/chat page,
 * the floating panel, the docked rail). Writes only when it was unread, and
 * refreshes every unread indicator when it was. No-op before 20261012c.
 */
export async function markTopicRead(userId, sessionId) {
  if (!userId || !sessionId) return false
  const { data, error } = await supabase
    .from('janet_chat_sessions')
    .update({ unread: false })
    .eq('id', sessionId)
    .eq('user_id', userId)
    .eq('unread', true)
    .select('id')
  if (error || !data?.length) return false
  notifySessionsChanged()
  return true
}

/** Marks the active topic read while a host is showing it (`visible`). */
export function useMarkVisibleTopicRead(sessionId, visible) {
  const { user } = useAuth()
  const userId = user?.id
  useEffect(() => {
    if (!visible || !userId || !sessionId) return
    markTopicRead(userId, sessionId)
  }, [visible, userId, sessionId])
}

export function useJanetUnread() {
  const { user } = useAuth()
  const userId = user?.id
  const [state, setState] = useState({ forUser: null, count: 0, latestId: null, latestTitle: null })

  const load = useCallback(async () => {
    if (!userId) return
    // The exact total (head: true — no rows, just the count PostgREST computed
    // over every matching row, never capped by a limit) beside the single
    // newest row, which is all that's needed to name the latest topic.
    const unreadOf = (q) => q.eq('user_id', userId).eq('unread', true).is('archived_at', null)
    const [{ count, error: countErr }, { data, error: dataErr }] = await Promise.all([
      unreadOf(supabase.from('janet_chat_sessions').select('id', { count: 'exact', head: true })),
      unreadOf(supabase.from('janet_chat_sessions').select('id, title'))
        .order('last_message_at', { ascending: false, nullsFirst: false })
        .limit(1),
    ])
    if (countErr || dataErr) return   // column not there yet, or transient — show nothing
    setState({ forUser: userId, count: count || 0, latestId: data?.[0]?.id || null, latestTitle: data?.[0]?.title || null })
  }, [userId])

  useEffect(() => {
    if (!userId) return undefined
    Promise.resolve().then(load)   // first read after mount, off the effect body
    const onChanged = () => load()
    const onStorage = (e) => { if (e.key === JANET_SESSIONS_TICK_KEY) load() }
    window.addEventListener('janet:sessions-changed', onChanged)
    window.addEventListener('storage', onStorage)
    const t = setInterval(load, POLL_MS)
    return () => {
      window.removeEventListener('janet:sessions-changed', onChanged)
      window.removeEventListener('storage', onStorage)
      clearInterval(t)
    }
  }, [load, userId])

  // Signed out, or still reading for a different account: nothing to show.
  if (!userId || state.forUser !== userId) return { count: 0, latestId: null, latestTitle: null }
  return { count: state.count, latestId: state.latestId, latestTitle: state.latestTitle }
}
